import React from 'react'
import { isEmpty, noop } from 'lodash-es'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { OrganisationSelectboxField } from 'view/shared/entity'
import { RadioButtonStripField } from 'view/components/buttons'
import {
  EntityStatus,
  ExtendedTradingEntityType,
  Organisation,
  OrganisationType,
  TradingEntity,
  TradingEntityType,
  ValidationIssue
} from 'api'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { TradingEntityTypeSelectboxField } from 'view/shared/entity/TradingEntityTypeSelectbox'
import { ToggleField } from 'view/components/toggle'
import { SelectboxField } from 'view/components/options'
import { CountrySelectboxField } from 'view/shared/entity/CountrySelectbox'
import { isOperatingCountryReadOnly } from './tradingEntityDialogUtils'
import { FeatureToggleTable } from '../../platform/PlatformSettings'

export interface TradingEntityDetailsPanelProps {
  tradingEntity: TradingEntity
  isNewTradingEntity: boolean
  getOrganisation: (organisationId: string) => Organisation | null
  readOnly: boolean
  onUpdate: (tradingEntity: Partial<TradingEntity>) => unknown
  validationIssues: ValidationIssue[]
}

const statusTranslations = {
  [EntityStatus.ENABLED]: Translation.TC_ACTIVE,
  [EntityStatus.DISABLED]: Translation.TC_SUSPENDED
}

export const TradingEntityDetailsPanel: React.FunctionComponent<TradingEntityDetailsPanelProps> = props => {
  const { tradingEntity, getOrganisation, isNewTradingEntity, readOnly, onUpdate, validationIssues } = props
  const organisation = getOrganisation(tradingEntity.organisationId)
  const statusTranslation = statusTranslations[tradingEntity.status]
  const gfidCodes = organisation?.gfidCodes ? organisation.gfidCodes : []

  const updateHandler = <K extends keyof TradingEntity>(key: K) => {
    return (value: TradingEntity[K]) => {
      const update = {
        [key]: typeof value === 'string' && isEmpty(value) ? null : value
      }
      onUpdate(update)
    }
  }

  const organisationIdUpdateHandler = (organisationId: string) => {
    const newOrganisation = getOrganisation(organisationId)
    onUpdate({
      organisationId,
      tradingEntityTypes: newOrganisation?.organisationTypes.includes(OrganisationType.BUY_SIDE)
        ? tradingEntity.tradingEntityTypes
        : [TradingEntityType.STANDARD],
      splitUsTrades: !newOrganisation?.organisationTypes.includes(OrganisationType.BUY_SIDE) ? false : tradingEntity.splitUsTrades
    })
  }

  const typeUpdateHandler = (tradingEntityTypeEnum: ExtendedTradingEntityType) => {
    switch (tradingEntityTypeEnum) {
      case ExtendedTradingEntityType.STANDARD:
        return updateHandler('tradingEntityTypes')([TradingEntityType.STANDARD])
      case ExtendedTradingEntityType.BLOCK:
        return updateHandler('tradingEntityTypes')([TradingEntityType.BLOCK])
      case ExtendedTradingEntityType.BOTH:
        updateHandler('tradingEntityTypes')([TradingEntityType.BLOCK, TradingEntityType.STANDARD])
        return
      default:
        throw new Error(`Unhandled tradingEntityTypeEnum: ${tradingEntityTypeEnum}`)
    }
  }

  const statusUpdateHandler = (value: Translation) => {
    updateHandler('status')(value === Translation.TC_ACTIVE ? EntityStatus.ENABLED : EntityStatus.DISABLED)
  }

  const getTradingEntityTypeValue = (tradingEntityTypes: TradingEntityType[]): ExtendedTradingEntityType | null => {
    if (!tradingEntityTypes || tradingEntityTypes.length === 0 || tradingEntityTypes.length > 2) {
      return null
    }

    if (tradingEntityTypes.length === 1) {
      switch (tradingEntityTypes[0]) {
        case TradingEntityType.BLOCK:
          return ExtendedTradingEntityType.BLOCK
        case TradingEntityType.STANDARD:
          return ExtendedTradingEntityType.STANDARD
        default:
          throw new Error(`Unhandled tradingEntityType: ${tradingEntityTypes[0]}`)
      }
    }

    return ExtendedTradingEntityType.BOTH
  }

  return (
    <>
      <DialogRow verticalLayout={constants.VerticalLayout.Bottom}>
        <DialogField>
          {isNewTradingEntity ? (
            <OrganisationSelectboxField
              dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-Organisation'}
              label={Locale.translate(Translation.SC_ORGANISATION)}
              placeholder={Locale.translate(Translation.SC_ORGANISATION_PLACEHOLDER)}
              value={tradingEntity.organisationId}
              readOnly={readOnly}
              onUpdate={organisationIdUpdateHandler}
              validationIssues={validationIssues}
              fieldName={'organisationId'}
            />
          ) : (
            <TextInputField
              label={Locale.translate(Translation.SC_ORGANISATION)}
              onUpdate={noop}
              readOnly={true}
              value={organisation ? organisation.legalName : ''}
              validationIssues={validationIssues}
              fieldName={'organisationId'}
            />
          )}
        </DialogField>
        <DialogField flex={0}>
          <RadioButtonStripField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-Trading'}
            label={Locale.translate(Translation.SC_TRADING)}
            options={[Translation.TC_ACTIVE, Translation.TC_SUSPENDED]}
            selectedOption={statusTranslation}
            buttonWidth={80}
            disabled={readOnly}
            onUpdate={statusUpdateHandler}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-ShortName'}
            label={Locale.translate(Translation.SC_SHORT_NAME)}
            value={tradingEntity.shortName}
            maxLength={constants.tradingEntityIdMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('shortName')}
            validationIssues={validationIssues}
            fieldName={'shortName'}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-FiccShortName'}
            label={Locale.translate(Translation.SC_FICC_SHORT_NAME)}
            value={tradingEntity.ficcShortName}
            maxLength={constants.tradingEntityIdMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('ficcShortName')}
            validationIssues={validationIssues}
            fieldName={'ficcShortName'}
          />
        </DialogField>
        <DialogField flex={0}>
          <TradingEntityTypeSelectboxField
            dataTestId={'TradingEntityDetailsPanel-TradingEntityTypeSelectboxField'}
            fieldName="tradingEntityTypes"
            label={Locale.translate(Translation.TC_TRADING_ENTITY_TYPE)}
            value={getTradingEntityTypeValue(tradingEntity.tradingEntityTypes)}
            readOnly={readOnly}
            onUpdate={typeUpdateHandler}
            disabled={!organisation?.organisationTypes.includes(OrganisationType.BUY_SIDE)}
            validationIssues={validationIssues}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-PublishedName'}
            label={Locale.translate(Translation.SC_PUBLISHED_NAME)}
            value={tradingEntity.publishedName}
            maxLength={constants.longNameMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('publishedName')}
            validationIssues={validationIssues}
            fieldName={'publishedName'}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-TradingEntityLEI'}
            label={Locale.translate(Translation.SC_TRADING_ENTITY_LEI)}
            value={tradingEntity.lei}
            maxLength={constants.leiMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('lei')}
            validationIssues={validationIssues}
            fieldName={'lei'}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'TradingEntityDetailsPanel-OrganisationSelectboxField-BIC'}
            label={Locale.translate(Translation.SC_BIC)}
            value={tradingEntity.bic}
            maxLength={constants.bicMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('bic')}
            validationIssues={validationIssues}
            fieldName={'bic'}
          />
        </DialogField>
        <DialogField>
          <CountrySelectboxField
            dataTestId={'TradingEntityDetailsPanel-CountrySelectboxField-Country'}
            label={Locale.translate(Translation.TC_OPERATING_COUNTRY)}
            value={tradingEntity.operatingCountry}
            readOnly={isOperatingCountryReadOnly(readOnly, tradingEntity)}
            onUpdate={updateHandler('operatingCountry')}
            validationIssues={validationIssues}
            fieldName={'operatingCountry'}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <SelectboxField
            label={Locale.translate(Translation.TC_EU_GFID)}
            fieldName={'euGfidCode'}
            value={tradingEntity.euGfidCode}
            optionKeys={gfidCodes}
            onUpdate={updateHandler('euGfidCode')}
            validationIssues={validationIssues}
            readOnly={readOnly}
            canClear={true}
          />
        </DialogField>
        <DialogField>
          <SelectboxField
            label={Locale.translate(Translation.TC_US_GFID)}
            fieldName={'usGfidCode'}
            value={tradingEntity.usGfidCode}
            optionKeys={gfidCodes}
            onUpdate={updateHandler('usGfidCode')}
            validationIssues={validationIssues}
            readOnly={readOnly}
            canClear={true}
          />
        </DialogField>
      </DialogRow>
      <FeatureToggleTable>
        <thead>
          <tr>
            <th>{Locale.translate(Translation.TC_FEATURE_TOGGLE)}</th>
            <th>{Locale.translate(Translation.TC_ENABLED)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{Locale.translate(Translation.TC_15A6_ELIGIBLE)}</td>
            <td>
              <ToggleField
                dataTestId={'TradingEntityDetailsPanel-ToggleField-eligible15a6'}
                fieldName="eligible15a6"
                label={''}
                checked={tradingEntity.eligible15a6}
                onUpdate={updateHandler('eligible15a6')}
                readOnly={readOnly}
                labelPosition={constants.LabelPosition.Left}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_COMBINED_EU_US_EOD_FILE)}</td>
            <td>
              <ToggleField
                dataTestId={'TradingEntityDetailsPanel-ToggleField-eligibleCombinedRecFile'}
                fieldName="eligibleCombinedRecFile"
                label={''}
                checked={tradingEntity.eligibleCombinedRecFile}
                onUpdate={updateHandler('eligibleCombinedRecFile')}
                readOnly={readOnly}
                labelPosition={constants.LabelPosition.Left}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_CENTRAL_BANK)}</td>
            <td>
              <ToggleField
                dataTestId={'TradingEntityDetailsPanel-ToggleField-centralBank'}
                fieldName="centralBank"
                label={''}
                checked={tradingEntity.centralBank}
                onUpdate={updateHandler('centralBank')}
                readOnly={readOnly}
                labelPosition={constants.LabelPosition.Left}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_NEGOTIATION_HISTORY_FILE)}</td>
            <td>
              <ToggleField
                dataTestId={'TradingEntityDetailsPanel-ToggleField-negotiationHistoryFile'}
                fieldName="negotiationHistoryFile"
                label={''}
                checked={tradingEntity.negotiationHistoryFile}
                onUpdate={updateHandler('negotiationHistoryFile')}
                readOnly={readOnly}
                labelPosition={constants.LabelPosition.Left}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_ROUND_PRICE_TO_EIGHT)}</td>
            <td>
              <ToggleField
                dataTestId={'TradingEntityDetailsPanel-ToggleField-roundPriceToOneEighth'}
                fieldName="roundPriceToOneEighth"
                label={''}
                checked={tradingEntity.roundPriceToOneEighth}
                onUpdate={updateHandler('roundPriceToOneEighth')}
                readOnly={readOnly}
                labelPosition={constants.LabelPosition.Left}
                disabled={!organisation?.organisationTypes.includes(OrganisationType.SELL_SIDE)}
              />
            </td>
          </tr>
        </tbody>
      </FeatureToggleTable>
    </>
  )
}
